import { studyQueryKeys } from '@/config/queryKeys';
import { STUDIES_QUERY_GC_TIME, STUDIES_QUERY_STALE_TIME, smIconSize } from '@/constants';
import { RunEditForm, type RunEditFormHandle } from '@/pages/Instrument_Runs/components/RunEditForm';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import type { InstrumentRunPublic, Org, PaginatedStudiesResponse } from '@/types';
import { FullLoading } from '@components/index';
import { Breadcrumbs } from '@nautilusbio/nb-design-system/Breadcrumbs';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, createLazyFileRoute, useLoaderData, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { CloudUpload, Save, X } from 'lucide-react';
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import styles from '../../pages/Experiments/New/styles/styles.module.css';
import irStyles from '../../pages/Instrument_Runs/styles.module.css';
import globalStyles from '../../styles/index.module.css';

export const Route = createLazyFileRoute('/instrument_runs/new')({
  component: RouteComponent,
  pendingComponent: () => {
    const { t } = useTranslation();
    return <FullLoading label={t('loading')} />;
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setShouldRefreshInstrumentRuns } = useAppStore(
    useShallow(state => ({ setShouldRefreshInstrumentRuns: state.setShouldRefreshInstrumentRuns })),
  );

  const loaderData = useLoaderData({ from: '/instrument_runs/new' }) as {
    orgId: string;
    mode: 'create' | 'draft' | 'edit';
    formInstrumentRunData: InstrumentRunPublic | null;
    organizations: Org[];
  };
  const { orgId, mode, formInstrumentRunData, organizations } = loaderData;
  const formRef = useRef<RunEditFormHandle>(null);

  const { data: studiesData } = useSuspenseQuery<PaginatedStudiesResponse>({
    queryKey: studyQueryKeys.list(orgId),
    queryFn: async () =>
      searchStudies(orgId, {
        skip: 0,
        limit: 500,
        sort_by: 'created_at',
        sort_order: 'desc',
      }),
    staleTime: STUDIES_QUERY_STALE_TIME,
    gcTime: STUDIES_QUERY_GC_TIME,
  });

  const studies = useMemo(() => {
    if (!studiesData || !Array.isArray(studiesData.records)) return [];
    return studiesData.records;
  }, [studiesData]);

  // Guard: wait for draft/edit data to load
  const [showError, setShowError] = useState(false);
  useEffect(() => {
    if ((mode === 'draft' || mode === 'edit') && !formInstrumentRunData) {
      const timer = setTimeout(() => setShowError(true), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [mode, formInstrumentRunData]);

  if (!orgId) {
    navigate({ to: '/organizations' });
    return null;
  }

  if ((mode === 'draft' || mode === 'edit') && !formInstrumentRunData) {
    if (!showError) return <FullLoading label={t('loading')} />;

    return (
      <Stack className={clsx(styles.container, globalStyles.scrollbars)}>
        <Text color="error">
          {t('create:run.errors.couldNotLoadDraft', {
            defaultValue: 'Could not load draft run. It may not exist or you may not have permission.',
          })}
        </Text>
        <Button
          data-testid="return-to-list-btn"
          onPress={async () => {
            setShouldRefreshInstrumentRuns(true);
            return navigate({ to: '/instrument_runs' });
          }}
        >
          {t('create:run.returnToList', { defaultValue: 'Return to Instrument Runs List' })}
        </Button>
      </Stack>
    );
  }

  if (studies.length === 0) {
    return (
      <Stack className={clsx(styles.container, globalStyles.scrollbars)}>
        <div className={styles.formContainer}>
          <div className={styles.messageContainer}>
            <Text>{t('create:errors.noStudiesAvailable')}</Text>
            <Button data-testid="create-new-study-btn">{t('create:run.clickToCreateStudy')}</Button>
          </div>
        </div>
      </Stack>
    );
  }

  return (
    <Stack className={irStyles.newRunLayout} fullHeight fullWidth gap={0} orientation="vertical">
      {/* Static header row — always visible */}
      <Stack
        alignItems="center"
        className={irStyles.headerActionRow}
        fullWidth
        justifyContent="space-between"
        orientation="horizontal"
      >
        <Breadcrumbs
          links={[
            { id: 'runs', children: t('runs:header.runs_label'), to: '/instrument_runs' },
            { id: 'current', children: formInstrumentRunData?.name ?? t('runs:new_run') },
          ]}
          renderLink={(link, _index, defaultLinkProps) => {
            if (!link?.to) return link?.children;
            return (
              <Link className={defaultLinkProps.className} to={link.to as '/instrument_runs'}>
                {link.children}
              </Link>
            );
          }}
          separator="arrow"
          size="md"
        />

        <Stack alignItems="center" gap={3} orientation="horizontal">
          <Button
            data-testid="run-action-cancel"
            onPress={() => void navigate({ to: '/instrument_runs' })}
            visual="unstyled"
          >
            <X size={smIconSize} />
            {t('general:cancel')}
          </Button>

          {(mode === 'create' || mode === 'draft') && (
            <Button data-testid="run-save-draft" onPress={() => formRef.current?.saveDraft()} visual="unstyled">
              <Save size={smIconSize} />
              {t('create:run.saveAsDraft')}
            </Button>
          )}

          <Button
            data-testid={mode === 'edit' ? 'run-save' : 'run-submit'}
            onPress={() => {
              if (mode === 'draft') {
                formRef.current?.submitDraft();
              } else {
                formRef.current?.submit();
              }
            }}
            visual="unstyled"
          >
            <CloudUpload size={smIconSize} />
            {mode === 'edit' ? t('general:save') : t('create:run.submit', { defaultValue: 'Submit' })}
          </Button>
        </Stack>
      </Stack>

      {/* Interior scrollable form area */}
      <div className={irStyles.formScrollArea}>
        <RunEditForm
          ref={formRef}
          showHeader
          mode={mode === 'draft' ? 'edit' : mode}
          orgId={orgId}
          organizations={organizations}
          run={formInstrumentRunData ?? undefined}
          studies={studies}
          onCancel={() => void navigate({ to: '/instrument_runs' })}
          onSave={() => {
            if (mode !== 'create') void navigate({ to: '/instrument_runs' });
          }}
        />
      </div>
    </Stack>
  );
}
